import { styled } from '@mui/material';
import type { ReactNode } from 'react';

import { DocumentIcon, NextIcon, OpenInNewIcon } from '../../../icons';
import type { ResourceColorPalette } from '../../../theme/tokens/resourcesColors';
import { Tag } from '../../atoms/Tag';
import { useCardResource } from '../CardResource';
import { MEETING_LIST_WIDE_QUERY, MEETING_TILE_SIZE } from '../MeetingList/meetingListContainer';
import { useMeetingListItem } from './useMeetingListItem';

const DEFAULT_NEW_TAB_LABEL = '(s’ouvre dans un nouvel onglet)';

type Props = {
  title: string;
  /** Start of the meeting. A midnight start is shown as a day without time. */
  date: string | Date;
  /** Upcoming meetings get the rendez-vous tint; past ones stay neutral. */
  upcoming?: boolean;
  /** Short countdown for an upcoming meeting, e.g. "Dans 3 jours". */
  badge?: string;
  /** Names the attached report, e.g. "Compte rendu". Omit when the meeting has none. */
  reportLabel?: string;
  /** Address of the report. The row then opens it in a new tab. */
  href?: string;
  /** Opens the meeting inside the app when there is no `href`. */
  onClick?: () => void;
  /** Secondary controls (resource menu). Rendered above the row target, never nested in it. */
  action?: ReactNode;
  /** Announced after the title to warn that the link leaves the app. */
  newTabLabel?: string;
  'data-testid'?: string;
};

type TileOwnerState = {
  resourceColor: ResourceColorPalette;
  upcoming: boolean;
};

const Row = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: `${MEETING_TILE_SIZE}px minmax(0, 1fr) auto`,
  gridTemplateAreas: `
    "tile title trailing"
    "tile meta trailing"`,
  columnGap: theme.spacing(1.5),
  rowGap: theme.spacing(0.5),
  alignItems: 'center',
  minHeight: theme.spacing(9),
  paddingBlock: theme.spacing(1.5),
  paddingInline: theme.spacing(2),
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '&:has(a:focus-visible), &:has(button[data-row-target]:focus-visible)': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
  [MEETING_LIST_WIDE_QUERY]: {
    gridTemplateColumns: `${MEETING_TILE_SIZE}px minmax(12rem, 1.4fr) minmax(0, 2fr) auto`,
    gridTemplateAreas: '"tile title meta trailing"',
    columnGap: theme.spacing(2),
    minHeight: theme.spacing(8),
    paddingBlock: theme.spacing(1),
  },
}));

const Tile = styled('span')<{ ownerState: TileOwnerState }>(({ theme, ownerState }) => ({
  gridArea: 'tile',
  alignSelf: 'start',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: MEETING_TILE_SIZE,
  height: MEETING_TILE_SIZE,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: ownerState.upcoming
    ? ownerState.resourceColor.light
    : theme.palette.background.elevation1,
  color: theme.palette.text.primary,
  fontVariantNumeric: 'tabular-nums',
  [MEETING_LIST_WIDE_QUERY]: {
    alignSelf: 'center',
  },
}));

const TileDay = styled('span')(({ theme }) => ({
  ...theme.typography.body1Emphasis,
  lineHeight: 1.1,
}));

const TileMonth = styled('span')<{ ownerState: TileOwnerState }>(({ theme, ownerState }) => ({
  ...theme.typography.caption,
  fontWeight: theme.typography.body2Emphasis.fontWeight,
  lineHeight: 1.1,
  color: ownerState.upcoming ? ownerState.resourceColor.main : theme.palette.text.secondary,
  textTransform: 'uppercase',
}));

const stretchedTarget = {
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
  },
  '&:focus-visible': {
    outline: 'none',
  },
} as const;

const TitleLink = styled('a')(({ theme }) => ({
  ...theme.typography.body1Emphasis,
  ...stretchedTarget,
  gridArea: 'title',
  minWidth: 0,
  color: theme.palette.text.primary,
  textDecoration: 'none',
  overflowWrap: 'anywhere',
}));

const TitleButton = styled('button')(({ theme }) => ({
  // Buttons do not inherit the font: reset it before applying the title style.
  font: 'inherit',
  fontFamily: theme.typography.fontFamily,
  ...theme.typography.body1Emphasis,
  ...stretchedTarget,
  gridArea: 'title',
  minWidth: 0,
  margin: 0,
  padding: 0,
  border: 0,
  background: 'none',
  color: theme.palette.text.primary,
  textAlign: 'left',
  cursor: 'pointer',
  overflowWrap: 'anywhere',
}));

const VisuallyHidden = styled('span')({
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
});

const Meta = styled('span')(({ theme }) => ({
  gridArea: 'meta',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: theme.spacing(1.5),
  rowGap: theme.spacing(0.5),
  minWidth: 0,
}));

const DateText = styled('time')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  fontVariantNumeric: 'tabular-nums',
}));

const DateShort = styled('span')({
  [MEETING_LIST_WIDE_QUERY]: {
    display: 'none',
  },
});

const DateLong = styled('span')({
  display: 'none',
  [MEETING_LIST_WIDE_QUERY]: {
    display: 'inline',
  },
});

const Report = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
}));

const Trailing = styled('span')(({ theme }) => ({
  gridArea: 'trailing',
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
}));

const ActionSlot = styled('span')(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  marginRight: theme.spacing(-1),
}));

/**
 * One council meeting of the register, inside a `MeetingList`.
 * A calendar tile (day + month) leads the row; the title is the row target (its hit area covers
 * the whole row) and either opens the report in a new tab (`href`) or the meeting in the app
 * (`onClick`). The optional action sits above it so it stays a separate target.
 */
export function MeetingListItem(props: Props) {
  const hook = useMeetingListItem(props);
  const { resourceColor } = useCardResource({ variant: 'rendezVous' });
  const ownerState = { resourceColor, upcoming: Boolean(props.upcoming) };
  const isLink = Boolean(props.href);

  const titleContent = (
    <>
      {props.title}
      <VisuallyHidden>{`, ${hook.dateLabel}`}</VisuallyHidden>
      {isLink ? <VisuallyHidden> {props.newTabLabel ?? DEFAULT_NEW_TAB_LABEL}</VisuallyHidden> : null}
    </>
  );

  return (
    <li data-testid={props['data-testid'] ?? 'meeting-list-item'}>
      <Row>
        <Tile aria-hidden="true" ownerState={ownerState}>
          <TileDay>{hook.dayNumber}</TileDay>
          <TileMonth ownerState={ownerState}>{hook.monthAbbr}</TileMonth>
        </Tile>
        {isLink ? (
          <TitleLink href={props.href} target="_blank" rel="noopener noreferrer">
            {titleContent}
          </TitleLink>
        ) : (
          <TitleButton type="button" data-row-target onClick={props.onClick}>
            {titleContent}
          </TitleButton>
        )}
        <Meta>
          <DateText dateTime={hook.dateTime}>
            <DateShort>{hook.shortDateLabel}</DateShort>
            <DateLong>{hook.dateLabel}</DateLong>
          </DateText>
          {props.badge ? <Tag label={props.badge} color="rendezVous" /> : null}
          {props.reportLabel ? (
            <Report>
              <DocumentIcon fontSize="inherit" aria-hidden="true" />
              {props.reportLabel}
            </Report>
          ) : null}
        </Meta>
        <Trailing>
          {props.action ? (
            <ActionSlot>{props.action}</ActionSlot>
          ) : isLink ? (
            <OpenInNewIcon fontSize="small" aria-hidden="true" />
          ) : (
            <NextIcon fontSize="small" aria-hidden="true" />
          )}
        </Trailing>
      </Row>
    </li>
  );
}

export type { Props as MeetingListItemProps };
